import { Classes, MaybeElement, Tag, TagProps } from "@blueprintjs/core";
import { ThemeType } from "@hex/common";
import { lighten, rgba } from "polished";
import React from "react";
import styled, { css } from "styled-components";

import { STATIC_ICON } from "../components/icons/staticIcons";
import sharedStyles from "../theme/common/sharedThemeStyles";

// "alpha" is a Hex-specific intent for early-access / preview surfaces. Blueprint's
// intent enum is fixed, so we apply this via a custom class instead.
const ALPHA_BLUE_LIGHT = sharedStyles.colors.COBALT4;
const ALPHA_BLUE_DARK = sharedStyles.colors.COBALT5;
const ALPHA_INTENT_CLASS = "hex-tag-intent-alpha";

export const tagStyles = css<{ $small?: boolean }>`
  &.${Classes.TAG}.${Classes.MINIMAL} {
    color: ${({ theme }) => theme.fontColor.DEFAULT};

    background-color: ${({ theme }) =>
      rgba(lighten(0.1, theme.fontColor.DEFAULT), 0.08)};

    &.${Classes.INTENT_PRIMARY} {
      color: ${({ theme }) => theme.intent.PRIMARY};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.PRIMARY), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.PRIMARY};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.PRIMARY, 0.7)};
      }
    }

    &.${Classes.INTENT_SUCCESS} {
      color: ${({ theme }) => theme.intent.SUCCESS};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.SUCCESS), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.SUCCESS};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.SUCCESS, 0.7)};
      }
    }

    &.${Classes.INTENT_WARNING} {
      color: ${({ theme }) => theme.intent.WARNING};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.WARNING), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.WARNING};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.WARNING, 0.7)};
      }
    }

    &.${Classes.INTENT_DANGER} {
      color: ${({ theme }) => theme.intent.DANGER};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.DANGER), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.DANGER};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.DANGER, 0.7)};
      }
    }

    &.${ALPHA_INTENT_CLASS} {
      color: ${({ theme }) =>
        theme.type === ThemeType.DARK ? ALPHA_BLUE_DARK : ALPHA_BLUE_LIGHT};
      background-color: ${({ theme }) =>
        rgba(
          theme.type === ThemeType.DARK ? ALPHA_BLUE_DARK : ALPHA_BLUE_LIGHT,
          0.1,
        )};

      .${Classes.ICON} > svg {
        color: ${({ theme }) =>
          theme.type === ThemeType.DARK ? ALPHA_BLUE_DARK : ALPHA_BLUE_LIGHT};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) =>
          rgba(
            theme.type === ThemeType.DARK ? ALPHA_BLUE_DARK : ALPHA_BLUE_LIGHT,
            0.7,
          )};
      }
    }

    ${({ $small }) =>
      $small &&
      css`
        font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
        line-height: 16px;
        height: 16px;
        min-height: 16px;
        padding: 0px 4px;
      `}

    &:has(> .${Classes.ICON}:first-child) {
      padding-left: 4px;
    }

    > .${Classes.ICON}:first-child {
      margin-right: 2px;
    }

    &:has(> .${Classes.ICON}:last-child) {
      padding-right: 4px;
    }

    > *:has(+ .${Classes.ICON}:last-child) {
      margin-right: 2px;
    }
  }

  &.${Classes.TAG} > .${Classes.TAG_REMOVE} {
    .${Classes.ICON} {
      background-color: ${({ theme }) => theme.fontColor.MUTED};
      background-image: none;
      mask-image: ${`url('data:image/svg+xml,${STATIC_ICON.CrossSmall}')`};
      svg {
        visibility: hidden;
      }
    }
  }

  ${({ $small }) =>
    $small &&
    css`
      .${Classes.ICON} > svg {
        width: 12px;
        height: 12px;
      }

      .${Classes.TAG_REMOVE} {
        margin-right: -4px !important;
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        mask-size: 12px 12px;
        mask-repeat: no-repeat;
        mask-position: center;
      }
    `}
`;

const StyledTag = styled(Tag)<{ $small?: boolean }>`
  ${tagStyles}
`;

/**
 * https://blueprintjs.com/docs/#core/components/tag
 */

interface HexTagUniqueProps {
  small?: boolean;
  /**
   * Blueprint uses the `title` prop to display a default tooltip.
   * Use disableTooltip if you want to wrap the tag into a HexTooltip, and do not want duplicate tooltips on the HexTag.
   */
  disableTooltip?: boolean;
}

export type HexTagIntent = TagProps["intent"] | "alpha";

export interface HexTagProps
  extends
    Omit<TagProps, "intent" | "ref" | "icon" | "rightIcon">,
    HexTagUniqueProps {
  intent?: HexTagIntent;
  icon?: MaybeElement;
  rightIcon?: MaybeElement;
}

export const HexTag = React.forwardRef<HTMLSpanElement, HexTagProps>(
  function HexTag({ className, disableTooltip, intent, small, ...props }, ref) {
    const isAlpha = intent === "alpha";
    return (
      <StyledTag
        {...props}
        ref={ref}
        $small={small}
        className={
          isAlpha
            ? [className, ALPHA_INTENT_CLASS].filter(Boolean).join(" ")
            : className
        }
        htmlTitle={disableTooltip ? "" : props.title}
        intent={isAlpha ? undefined : intent}
        minimal={true}
      />
    );
  },
);
